import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    FiArrowLeft,
    FiCheckCircle,
    FiClock,
    FiCreditCard,
    FiEdit2,
    FiFileText,
    FiLoader,
    FiPackage,
    FiPrinter,
    FiRefreshCw,
    FiXCircle,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
    useParams,
} from "react-router-dom";

import {
    clearSelectedPurchaseBill,
    confirmPurchaseBill,
    fetchPurchaseBillById,
    purchaseBillErrorSelector,
    purchaseBillLoadingSelector,
    selectedPurchaseBillSelector,
    cancelPurchaseBill,
} from "../../../../../../store/billing/purchaseBills/purchaseBillSlice";

import MakePaymentDialog from "../PaymentOut/components/MakePaymentDialog";
import DebitNotesPanel from "../DebitNotes/components/DebitNotePanel";

import "./PurchaseBills.css";
import PurchaseBillCancelDialog from "./components/PurchaseBillCancelDialog";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(number)
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
        100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        roundMoney(value)
    );

const formatDate = (
    value,
    includeTime = false
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        includeTime
            ? {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            }
            : {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }
    ).format(date);
};

const formatLabel = (
    value
) =>
    String(
        value || "-"
    )
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );

const getSupplierId = (
    bill
) => {
    if (
        bill?.supplier &&
        typeof bill.supplier ===
        "object"
    ) {
        return (
            bill.supplier?._id ||
            bill.supplier?.id ||
            ""
        );
    }

    return (
        bill?.supplier ||
        ""
    );
};

const getSupplierName = (
    bill
) =>
    bill
        ?.supplierSnapshot
        ?.supplierName ||
    bill
        ?.supplierSnapshot
        ?.companyName ||
    bill
        ?.supplier
        ?.supplierName ||
    bill
        ?.supplier
        ?.name ||
    "Supplier";

const getSupplierMobile = (
    bill
) =>
    bill
        ?.supplierSnapshot
        ?.mobile ||
    bill
        ?.supplier
        ?.mobile ||
    "-";

const getSupplierEmail = (
    bill
) =>
    bill
        ?.supplierSnapshot
        ?.email ||
    bill
        ?.supplier
        ?.email ||
    "-";

const getGrandTotal = (
    bill
) =>
    roundMoney(
        bill
            ?.totals
            ?.grandTotal ??
        bill
            ?.grandTotal ??
        0
    );

const getPaidAmount = (
    bill
) =>
    roundMoney(
        bill
            ?.totals
            ?.paidAmount ??
        bill
            ?.paidAmount ??
        0
    );

const getDebitNoteAmount = (
    bill
) =>
    roundMoney(
        bill
            ?.totals
            ?.debitNoteAmount ??
        bill
            ?.debitNoteAmount ??
        0
    );

const getAdjustedGrandTotal = (
    bill
) =>
    roundMoney(
        bill
            ?.totals
            ?.adjustedGrandTotal ??
        Math.max(
            getGrandTotal(
                bill
            ) -
            getDebitNoteAmount(
                bill
            ),
            0
        )
    );

const getBalanceAmount = (
    bill
) =>
    roundMoney(
        bill
            ?.totals
            ?.balanceAmount ??
        bill
            ?.balanceAmount ??
        Math.max(
            getAdjustedGrandTotal(
                bill
            ) -
            getPaidAmount(
                bill
            ),
            0
        )
    );

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const PurchaseBillViewPage = () => {
    const printRef =
        useRef(null);

    const dispatch =
        useDispatch();

    const history =
        useHistory();

    const {
        id,
    } =
        useParams();

    const purchaseBill =
        useSelector(
            selectedPurchaseBillSelector
        );

    const loading =
        useSelector(
            purchaseBillLoadingSelector
        ) || {};

    const errors =
        useSelector(
            purchaseBillErrorSelector
        ) || {};

    const [
        activeTab,
        setActiveTab,
    ] = useState(
        "overview"
    );

    const [
        paymentDialogOpen,
        setPaymentDialogOpen,
    ] = useState(false);

    const [
        localError,
        setLocalError,
    ] = useState("");

    const [
        cancelDialogOpen,
        setCancelDialogOpen,
    ] = useState(false);

    const [
        cancellationReason,
        setCancellationReason,
    ] = useState("");

    /*
    |--------------------------------------------------------------------------
    | LOAD
    |--------------------------------------------------------------------------
    */
    const handleCancelPurchaseBill =
        async (
            cancellationReason
        ) => {
            if (
                !purchaseBill?._id
            ) {
                throw new Error(
                    "Purchase bill ID is missing."
                );
            }

            try {
                setLocalError("");

                await dispatch(
                    cancelPurchaseBill({
                        purchaseBillId:
                            purchaseBill._id,

                        cancellationReason,
                    })
                ).unwrap();

                setCancelDialogOpen(
                    false
                );

                await dispatch(
                    fetchPurchaseBillById(
                        purchaseBill._id
                    )
                ).unwrap();
            } catch (error) {
                const message =
                    typeof error ===
                        "string"
                        ? error
                        : error?.message ||
                        "Unable to cancel purchase bill.";

                setLocalError(
                    message
                );

                /*
                |--------------------------------------------------------------------------
                | IMPORTANT
                |--------------------------------------------------------------------------
                |
                | Throw it again so PurchaseBillCancelDialog can display the error
                | without closing itself.
                |--------------------------------------------------------------------------
                */

                throw new Error(
                    message
                );
            }
        };

    const loadPurchaseBill =
        useCallback(
            async () => {
                if (!id) {
                    return;
                }

                try {
                    setLocalError("");

                    await dispatch(
                        fetchPurchaseBillById(
                            id
                        )
                    ).unwrap();
                } catch (
                error
                ) {
                    setLocalError(
                        typeof error ===
                            "string"
                            ? error
                            : "Unable to load purchase bill."
                    );
                }
            },
            [
                dispatch,
                id,
            ]
        );

    useEffect(
        () => {
            loadPurchaseBill();

            return () => {
                dispatch(
                    clearSelectedPurchaseBill()
                );
            };
        },
        [
            loadPurchaseBill,
            dispatch,
        ]
    );

    /*
    |--------------------------------------------------------------------------
    | STATUS
    |--------------------------------------------------------------------------
    */

    const documentStatus =
        String(
            purchaseBill
                ?.documentStatus ||
            purchaseBill
                ?.status ||
            "DRAFT"
        ).toUpperCase();

    const paymentStatus =
        String(
            purchaseBill
                ?.paymentStatus ||
            "UNPAID"
        ).toUpperCase();

    const isDraft =
        documentStatus ===
        "DRAFT";

    const isConfirmed =
        documentStatus ===
        "CONFIRMED";

    const canMakePayment =
        isConfirmed &&
        getBalanceAmount(
            purchaseBill
        ) > 0;

    const canCreateDebitNote =
        isConfirmed;

    /*
    |--------------------------------------------------------------------------
    | ITEMS
    |--------------------------------------------------------------------------
    */

    const items =
        useMemo(
            () =>
                Array.isArray(
                    purchaseBill
                        ?.items
                )
                    ? purchaseBill.items
                    : [],
            [
                purchaseBill,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | CONFIRM
    |--------------------------------------------------------------------------
    */

    const handleConfirm =
        async () => {
            if (
                !id ||
                !isDraft
            ) {
                return;
            }

            const approved =
                window.confirm(
                    `Confirm purchase bill ${purchaseBill
                        ?.documentNumber ||
                    ""
                    }?\n\nThis will add inventory stock and post the supplier ledger.`
                );

            if (!approved) {
                return;
            }

            try {
                setLocalError("");

                await dispatch(
                    confirmPurchaseBill(
                        id
                    )
                ).unwrap();

                await loadPurchaseBill();
            } catch (
            error
            ) {
                setLocalError(
                    typeof error ===
                        "string"
                        ? error
                        : "Unable to confirm purchase bill."
                );
            }
        };

    /*
    |--------------------------------------------------------------------------
    | PAYMENT SUCCESS
    |--------------------------------------------------------------------------
    */

    const handlePaymentSuccess =
        async () => {
            setPaymentDialogOpen(
                false
            );

            await loadPurchaseBill();
        };

    /*
    |--------------------------------------------------------------------------
    | SUPPLIER FOR PAYMENT
    |--------------------------------------------------------------------------
    */

    const paymentSupplier =
        useMemo(
            () => {
                const supplierId =
                    getSupplierId(
                        purchaseBill
                    );

                if (!supplierId) {
                    return null;
                }

                return {
                    _id:
                        supplierId,

                    supplierName:
                        getSupplierName(
                            purchaseBill
                        ),

                    companyName:
                        purchaseBill
                            ?.supplierSnapshot
                            ?.companyName ||
                        "",

                    mobile:
                        getSupplierMobile(
                            purchaseBill
                        ),

                    email:
                        getSupplierEmail(
                            purchaseBill
                        ),

                    gstin:
                        purchaseBill
                            ?.supplierSnapshot
                            ?.gstin ||
                        "",

                    pan:
                        purchaseBill
                            ?.supplierSnapshot
                            ?.pan ||
                        "",
                };
            },
            [
                purchaseBill,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | PRINT PURCHASE BILL
    |--------------------------------------------------------------------------
    |
    | Prints the actual rendered Purchase Bill page in the current document.
    | No iframe and no popup.
    |
    | The parent chain is temporarily neutralized so Admin Console wrappers
    | cannot clip, offset or hide the purchase bill in Chrome print preview.
    |--------------------------------------------------------------------------
    */

    const handlePrint =
        useCallback(
            async () => {
                const printableNode =
                    printRef.current;

                if (!printableNode) {
                    return false;
                }

                const touchedElements =
                    [];

                let current =
                    printableNode;

                while (
                    current &&
                    current !==
                    document.body
                ) {
                    touchedElements.push({
                        element:
                            current,

                        cssText:
                            current.getAttribute(
                                "style"
                            ),
                    });

                    current =
                        current.parentElement;
                }

                const forcePrintable =
                    (
                        element
                    ) => {
                        if (!element) {
                            return;
                        }

                        element.style.setProperty(
                            "display",
                            "block",
                            "important"
                        );

                        element.style.setProperty(
                            "visibility",
                            "visible",
                            "important"
                        );

                        element.style.setProperty(
                            "opacity",
                            "1",
                            "important"
                        );

                        element.style.setProperty(
                            "height",
                            "auto",
                            "important"
                        );

                        element.style.setProperty(
                            "min-height",
                            "0",
                            "important"
                        );

                        element.style.setProperty(
                            "max-height",
                            "none",
                            "important"
                        );

                        element.style.setProperty(
                            "overflow",
                            "visible",
                            "important"
                        );

                        element.style.setProperty(
                            "transform",
                            "none",
                            "important"
                        );
                    };

                touchedElements.forEach(
                    ({
                        element,
                    }) => {
                        forcePrintable(
                            element
                        );
                    }
                );

                document.body.classList.add(
                    "ckw-purchase-bill-printing"
                );

                let cleanedUp =
                    false;

                const cleanup =
                    () => {
                        if (cleanedUp) {
                            return;
                        }

                        cleanedUp =
                            true;

                        window.removeEventListener(
                            "afterprint",
                            cleanup
                        );

                        document.body.classList.remove(
                            "ckw-purchase-bill-printing"
                        );

                        touchedElements.forEach(
                            ({
                                element,
                                cssText,
                            }) => {
                                if (
                                    cssText ===
                                    null
                                ) {
                                    element.removeAttribute(
                                        "style"
                                    );
                                } else {
                                    element.setAttribute(
                                        "style",
                                        cssText
                                    );
                                }
                            }
                        );
                    };

                window.addEventListener(
                    "afterprint",
                    cleanup,
                    {
                        once:
                            true,
                    }
                );

                try {
                    if (
                        document.fonts?.ready
                    ) {
                        await document
                            .fonts
                            .ready;
                    }

                    const images =
                        Array.from(
                            printableNode
                                .querySelectorAll(
                                    "img"
                                )
                        );

                    await Promise.all(
                        images.map(
                            (
                                image
                            ) => {
                                if (
                                    image.complete
                                ) {
                                    return Promise.resolve();
                                }

                                return new Promise(
                                    (
                                        resolve
                                    ) => {
                                        const done =
                                            () => {
                                                image.removeEventListener(
                                                    "load",
                                                    done
                                                );

                                                image.removeEventListener(
                                                    "error",
                                                    done
                                                );

                                                resolve();
                                            };

                                        image.addEventListener(
                                            "load",
                                            done
                                        );

                                        image.addEventListener(
                                            "error",
                                            done
                                        );
                                    }
                                );
                            }
                        )
                    );

                    void printableNode.offsetHeight;

                    await new Promise(
                        (
                            resolve
                        ) =>
                            window.requestAnimationFrame(
                                () =>
                                    window.requestAnimationFrame(
                                        resolve
                                    )
                            )
                    );

                    window.focus();
                    window.print();

                    window.setTimeout(
                        cleanup,
                        60000
                    );

                    return true;
                } catch (
                    error
                ) {
                    cleanup();

                    return false;
                }
            },
            []
        );

    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    if (
        loading.details &&
        !purchaseBill
    ) {
        return (
            <div className="purchase-bill-page">
                <div className="purchase-bill-empty">
                    <FiLoader className="purchase-bill-spinner" />

                    <h3>
                        Loading purchase bill...
                    </h3>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={printRef}
            className="purchase-bill-page"
        >
            {/* HEADER */}

            <header className="purchase-bill-header">
                <div className="purchase-bill-heading">
                    <button
                        type="button"
                        className="purchase-bill-icon-button purchase-bill-no-print"
                        onClick={() =>
                            history.push(
                                "/billing/purchase-bills"
                            )
                        }
                    >
                        <FiArrowLeft />
                    </button>

                    <span className="purchase-bill-heading-icon">
                        <FiFileText />
                    </span>

                    <div>
                        <small>
                            PURCHASE BILL
                        </small>

                        <h1>
                            {purchaseBill
                                ?.documentNumber ||
                                purchaseBill
                                    ?.purchaseBillNumber ||
                                "Purchase Bill"}
                        </h1>

                        <p>
                            Supplier:{" "}
                            <strong>
                                {getSupplierName(
                                    purchaseBill
                                )}
                            </strong>
                        </p>
                    </div>
                </div>

                <div className="purchase-bill-header-actions purchase-bill-no-print">
                    <button
                        type="button"
                        className="purchase-bill-button"
                        disabled={
                            loading.details
                        }
                        onClick={
                            loadPurchaseBill
                        }
                    >
                        <FiRefreshCw
                            className={
                                loading.details
                                    ? "purchase-bill-spinner"
                                    : ""
                            }
                        />

                        Refresh
                    </button>

                    <button
                        type="button"
                        className="purchase-bill-button"
                        onClick={
                            handlePrint
                        }
                    >
                        <FiPrinter />

                        Print
                    </button>

                    {isDraft && (
                        <>
                            <button
                                type="button"
                                className="purchase-bill-button"
                                onClick={() =>
                                    history.push(
                                        `/billing/purchase-bills/${id}/edit`
                                    )
                                }
                            >
                                <FiEdit2 />

                                Edit
                            </button>

                            <button
                                type="button"
                                className="purchase-bill-button purchase-bill-button-primary"
                                disabled={
                                    loading.confirm
                                }
                                onClick={
                                    handleConfirm
                                }
                            >
                                {loading.confirm ? (
                                    <FiLoader className="purchase-bill-spinner" />
                                ) : (
                                    <FiCheckCircle />
                                )}

                                Confirm
                            </button>
                        </>
                    )}

                    {canMakePayment && (
                        <button
                            type="button"
                            className="purchase-bill-button purchase-bill-button-success"
                            onClick={() =>
                                setPaymentDialogOpen(
                                    true
                                )
                            }
                        >
                            <FiCreditCard />

                            Make Payment
                        </button>

                    )}
                    <button
                        type="button"
                        className="purchase-bill-button purchase-bill-button-danger"
                        disabled={
                            loading?.cancel
                        }
                        onClick={() =>
                            setCancelDialogOpen(
                                true
                            )
                        }
                    >
                        Cancel Bill
                    </button>
                </div>
            </header>

            {/* ERROR */}

            {(localError ||
                errors.details ||
                errors.confirm) && (
                    <div className="purchase-bill-alert">
                        {localError ||
                            errors.details ||
                            errors.confirm}
                    </div>
                )}

            {/* SUMMARY */}

            <section className="purchase-bill-summary-grid">
                <div className="purchase-bill-summary-card purchase-bill-summary-purchase">
                    <span>
                        Bill Amount
                    </span>

                    <strong>
                        {formatCurrency(
                            getGrandTotal(
                                purchaseBill
                            )
                        )}
                    </strong>

                    <small>
                        Original purchase value
                    </small>
                </div>

                <div className="purchase-bill-summary-card purchase-bill-summary-debit-note">
                    <span>
                        Debit Notes
                    </span>

                    <strong>
                        {formatCurrency(
                            getDebitNoteAmount(
                                purchaseBill
                            )
                        )}
                    </strong>

                    <small>
                        Purchase return adjustment
                    </small>
                </div>

                <div className="purchase-bill-summary-card purchase-bill-summary-paid">
                    <span>
                        Paid
                    </span>

                    <strong>
                        {formatCurrency(
                            getPaidAmount(
                                purchaseBill
                            )
                        )}
                    </strong>

                    <small>
                        Supplier payments
                    </small>
                </div>

                <div className="purchase-bill-summary-card purchase-bill-summary-outstanding">
                    <span>
                        Balance Due
                    </span>

                    <strong>
                        {formatCurrency(
                            getBalanceAmount(
                                purchaseBill
                            )
                        )}
                    </strong>

                    <small>
                        Outstanding supplier payable
                    </small>
                </div>
            </section>

            {/* STATUS BAR */}

            <section className="purchase-bill-status-bar">
                <div>
                    <span>
                        Document
                    </span>

                    <strong
                        className={`purchase-bill-status purchase-bill-status-${documentStatus.toLowerCase()}`}
                    >
                        {documentStatus ===
                            "CONFIRMED" ? (
                            <FiCheckCircle />
                        ) : documentStatus ===
                            "CANCELLED" ? (
                            <FiXCircle />
                        ) : (
                            <FiClock />
                        )}

                        {formatLabel(
                            documentStatus
                        )}
                    </strong>
                </div>

                <div>
                    <span>
                        Payment
                    </span>

                    <strong
                        className={`purchase-bill-payment-status purchase-bill-payment-status-${paymentStatus.toLowerCase()}`}
                    >
                        {paymentStatus ===
                            "PAID" ? (
                            <FiCheckCircle />
                        ) : paymentStatus ===
                            "PARTIALLY_PAID" ? (
                            <FiClock />
                        ) : (
                            <FiXCircle />
                        )}

                        {formatLabel(
                            paymentStatus
                        )}
                    </strong>
                </div>

                <div>
                    <span>
                        Stock
                    </span>

                    <strong>
                        {purchaseBill
                            ?.stockPosted ? (
                            <>
                                <FiPackage />

                                Posted
                            </>
                        ) : (
                            <>
                                <FiClock />

                                Pending
                            </>
                        )}
                    </strong>
                </div>

                <div>
                    <span>
                        Supplier Ledger
                    </span>

                    <strong>
                        {purchaseBill
                            ?.ledgerPosted ? (
                            <>
                                <FiCheckCircle />

                                Posted
                            </>
                        ) : (
                            <>
                                <FiClock />

                                Pending
                            </>
                        )}
                    </strong>
                </div>
            </section>

            {/* TABS */}

            <section className="purchase-bill-tabs purchase-bill-no-print">
                <button
                    type="button"
                    className={
                        activeTab ===
                            "overview"
                            ? "active"
                            : ""
                    }
                    onClick={() =>
                        setActiveTab(
                            "overview"
                        )
                    }
                >
                    Overview
                </button>

                <button
                    type="button"
                    className={
                        activeTab ===
                            "items"
                            ? "active"
                            : ""
                    }
                    onClick={() =>
                        setActiveTab(
                            "items"
                        )
                    }
                >
                    Items ({items.length})
                </button>

                <button
                    type="button"
                    className={
                        activeTab ===
                            "debit-notes"
                            ? "active"
                            : ""
                    }
                    onClick={() =>
                        setActiveTab(
                            "debit-notes"
                        )
                    }
                >
                    Debit Notes
                </button>
            </section>

            {/* OVERVIEW */}

            {activeTab ===
                "overview" && (
                    <>
                        <section className="purchase-bill-view-card">
                            <div className="purchase-bill-section-title">
                                <h3>
                                    Bill Information
                                </h3>

                                <p>
                                    Supplier invoice and purchase document details.
                                </p>
                            </div>

                            <div className="purchase-bill-view-info-grid">
                                <div>
                                    <span>
                                        Purchase Bill
                                    </span>

                                    <strong>
                                        {purchaseBill
                                            ?.documentNumber ||
                                            "-"}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Bill Date
                                    </span>

                                    <strong>
                                        {formatDate(
                                            purchaseBill
                                                ?.documentDate
                                        )}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Supplier Invoice
                                    </span>

                                    <strong>
                                        {purchaseBill
                                            ?.supplierInvoiceNumber ||
                                            "-"}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Supplier Invoice Date
                                    </span>

                                    <strong>
                                        {formatDate(
                                            purchaseBill
                                                ?.supplierInvoiceDate
                                        )}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Due Date
                                    </span>

                                    <strong>
                                        {formatDate(
                                            purchaseBill
                                                ?.dueDate
                                        )}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Financial Year
                                    </span>

                                    <strong>
                                        {purchaseBill
                                            ?.financialYear ||
                                            "-"}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Tax Type
                                    </span>

                                    <strong>
                                        {formatLabel(
                                            purchaseBill
                                                ?.taxType
                                        )}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Place of Supply
                                    </span>

                                    <strong>
                                        {purchaseBill
                                            ?.placeOfSupply ||
                                            "-"}
                                    </strong>
                                </div>
                            </div>
                        </section>

                        <section className="purchase-bill-view-card">
                            <div className="purchase-bill-section-title">
                                <h3>
                                    Supplier Details
                                </h3>
                            </div>

                            <div className="purchase-bill-view-info-grid">
                                <div>
                                    <span>
                                        Supplier
                                    </span>

                                    <strong>
                                        {getSupplierName(
                                            purchaseBill
                                        )}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Mobile
                                    </span>

                                    <strong>
                                        {getSupplierMobile(
                                            purchaseBill
                                        )}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Email
                                    </span>

                                    <strong>
                                        {getSupplierEmail(
                                            purchaseBill
                                        )}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        Company
                                    </span>

                                    <strong>
                                        {purchaseBill
                                            ?.supplierSnapshot
                                            ?.companyName ||
                                            "-"}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        GSTIN
                                    </span>

                                    <strong>
                                        {purchaseBill
                                            ?.supplierSnapshot
                                            ?.gstin ||
                                            "-"}
                                    </strong>
                                </div>

                                <div>
                                    <span>
                                        PAN
                                    </span>

                                    <strong>
                                        {purchaseBill
                                            ?.supplierSnapshot
                                            ?.pan ||
                                            "-"}
                                    </strong>
                                </div>
                            </div>
                        </section>

                        <section className="purchase-bill-view-card">
                            <div className="purchase-bill-section-title">
                                <h3>
                                    Amount Summary
                                </h3>
                            </div>

                            <div className="purchase-bill-view-total-wrapper">
                                <div className="purchase-bill-total-box">
                                    <div>
                                        <span>
                                            Gross Amount
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                purchaseBill
                                                    ?.totals
                                                    ?.grossAmount
                                            )}
                                        </strong>
                                    </div>

                                    <div>
                                        <span>
                                            Discount
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                purchaseBill
                                                    ?.totals
                                                    ?.discountAmount
                                            )}
                                        </strong>
                                    </div>

                                    <div>
                                        <span>
                                            Taxable Amount
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                purchaseBill
                                                    ?.totals
                                                    ?.taxableAmount
                                            )}
                                        </strong>
                                    </div>

                                    {purchaseBill
                                        ?.taxType ===
                                        "IGST" ? (
                                        <div>
                                            <span>
                                                IGST
                                            </span>

                                            <strong>
                                                {formatCurrency(
                                                    purchaseBill
                                                        ?.totals
                                                        ?.igstAmount
                                                )}
                                            </strong>
                                        </div>
                                    ) : (
                                        <>
                                            <div>
                                                <span>
                                                    CGST
                                                </span>

                                                <strong>
                                                    {formatCurrency(
                                                        purchaseBill
                                                            ?.totals
                                                            ?.cgstAmount
                                                    )}
                                                </strong>
                                            </div>

                                            <div>
                                                <span>
                                                    SGST
                                                </span>

                                                <strong>
                                                    {formatCurrency(
                                                        purchaseBill
                                                            ?.totals
                                                            ?.sgstAmount
                                                    )}
                                                </strong>
                                            </div>
                                        </>
                                    )}

                                    <div>
                                        <span>
                                            Round Off
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                purchaseBill
                                                    ?.totals
                                                    ?.roundOffAmount
                                            )}
                                        </strong>
                                    </div>

                                    <div className="purchase-bill-total-final">
                                        <span>
                                            Grand Total
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                getGrandTotal(
                                                    purchaseBill
                                                )
                                            )}
                                        </strong>
                                    </div>

                                    {getDebitNoteAmount(
                                        purchaseBill
                                    ) >
                                        0 && (
                                            <div>
                                                <span>
                                                    Less: Debit Notes
                                                </span>

                                                <strong className="purchase-bill-debit-note">
                                                    -
                                                    {formatCurrency(
                                                        getDebitNoteAmount(
                                                            purchaseBill
                                                        )
                                                    )}
                                                </strong>
                                            </div>
                                        )}

                                    <div>
                                        <span>
                                            Adjusted Total
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                getAdjustedGrandTotal(
                                                    purchaseBill
                                                )
                                            )}
                                        </strong>
                                    </div>

                                    <div>
                                        <span>
                                            Paid Amount
                                        </span>

                                        <strong className="purchase-bill-paid">
                                            {formatCurrency(
                                                getPaidAmount(
                                                    purchaseBill
                                                )
                                            )}
                                        </strong>
                                    </div>

                                    <div className="purchase-bill-total-balance">
                                        <span>
                                            Balance Due
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                getBalanceAmount(
                                                    purchaseBill
                                                )
                                            )}
                                        </strong>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {(purchaseBill?.notes ||
                            purchaseBill
                                ?.internalNotes) && (
                                <section className="purchase-bill-view-card">
                                    <div className="purchase-bill-section-title">
                                        <h3>
                                            Notes
                                        </h3>
                                    </div>

                                    {purchaseBill
                                        ?.notes && (
                                            <div className="purchase-bill-note-box">
                                                {
                                                    purchaseBill
                                                        .notes
                                                }
                                            </div>
                                        )}

                                    {purchaseBill
                                        ?.internalNotes && (
                                            <div className="purchase-bill-note-box">
                                                <strong>
                                                    Internal:
                                                </strong>{" "}
                                                {
                                                    purchaseBill
                                                        .internalNotes
                                                }
                                            </div>
                                        )}
                                </section>
                            )}
                    </>
                )}

            {/* ITEMS */}

            {activeTab ===
                "items" && (
                    <section className="purchase-bill-view-card">
                        <div className="purchase-bill-section-title">
                            <h3>
                                Purchase Items
                            </h3>

                            <p>
                                Items received from the supplier.
                            </p>
                        </div>

                        <div className="purchase-bill-items-table-wrapper">
                            <table className="purchase-bill-items-table">
                                <thead>
                                    <tr>
                                        <th>
                                            #
                                        </th>

                                        <th>
                                            Item
                                        </th>

                                        <th>
                                            HSN
                                        </th>

                                        <th className="text-right">
                                            Qty
                                        </th>

                                        <th className="text-right">
                                            Rate
                                        </th>

                                        <th className="text-right">
                                            Gross
                                        </th>

                                        <th className="text-right">
                                            Discount
                                        </th>

                                        <th className="text-right">
                                            Taxable
                                        </th>

                                        <th className="text-right">
                                            Tax
                                        </th>

                                        <th className="text-right">
                                            Total
                                        </th>

                                        <th>
                                            Stock
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {items.map(
                                        (
                                            item,
                                            index
                                        ) => (
                                            <tr
                                                key={
                                                    item
                                                        ?._id ||
                                                    index
                                                }
                                            >
                                                <td>
                                                    {index +
                                                        1}
                                                </td>

                                                <td>
                                                    <div className="purchase-bill-item-name">
                                                        <strong>
                                                            {item
                                                                ?.itemName ||
                                                                "-"}
                                                        </strong>

                                                        {item
                                                            ?.description && (
                                                                <span>
                                                                    {
                                                                        item.description
                                                                    }
                                                                </span>
                                                            )}
                                                    </div>
                                                </td>

                                                <td>
                                                    {item
                                                        ?.hsnSac ||
                                                        "-"}
                                                </td>

                                                <td className="text-right">
                                                    {toNumber(
                                                        item
                                                            ?.quantity
                                                    )}
                                                </td>

                                                <td className="text-right">
                                                    {formatCurrency(
                                                        item
                                                            ?.rate
                                                    )}
                                                </td>

                                                <td className="text-right">
                                                    {formatCurrency(
                                                        item
                                                            ?.grossAmount
                                                    )}
                                                </td>

                                                <td className="text-right">
                                                    {formatCurrency(
                                                        item
                                                            ?.discountAmount
                                                    )}
                                                </td>

                                                <td className="text-right">
                                                    {formatCurrency(
                                                        item
                                                            ?.taxableAmount
                                                    )}
                                                </td>

                                                <td className="text-right">
                                                    <div>
                                                        {formatCurrency(
                                                            item
                                                                ?.taxAmount
                                                        )}

                                                        <small className="purchase-bill-tax-rate">
                                                            {toNumber(
                                                                item
                                                                    ?.taxRate
                                                            )}
                                                            %
                                                        </small>
                                                    </div>
                                                </td>

                                                <td className="text-right">
                                                    <strong className="purchase-bill-amount">
                                                        {formatCurrency(
                                                            item
                                                                ?.totalAmount
                                                        )}
                                                    </strong>
                                                </td>

                                                <td>
                                                    {item
                                                        ?.stockAdded ? (
                                                        <span className="purchase-bill-stock-posted">
                                                            <FiCheckCircle />

                                                            Added
                                                        </span>
                                                    ) : (
                                                        <span className="purchase-bill-stock-pending">
                                                            <FiClock />

                                                            Pending
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}

            {/* DEBIT NOTES */}

            {activeTab ===
                "debit-notes" && (
                    <>
                        {!canCreateDebitNote ? (
                            <div className="purchase-bill-info-message">
                                Confirm this purchase bill before creating a debit note.
                            </div>
                        ) : (
                            <DebitNotesPanel
                                purchaseBill={
                                    purchaseBill
                                }
                                onPurchaseBillUpdated={
                                    loadPurchaseBill
                                }
                            />
                        )}
                    </>
                )}

            {/* PAYMENT */}

            <MakePaymentDialog
                open={
                    paymentDialogOpen
                }
                preselectedSupplier={
                    paymentSupplier
                }
                onClose={() =>
                    setPaymentDialogOpen(
                        false
                    )
                }
                onSuccess={
                    handlePaymentSuccess
                }
            />
            <PurchaseBillCancelDialog
                open={
                    cancelDialogOpen
                }
                purchaseBill={
                    purchaseBill
                }
                loading={
                    Boolean(
                        loading?.cancel
                    )
                }
                // error={
                //     error?.cancel ||
                //     ""
                // }
                onClose={() =>
                    setCancelDialogOpen(
                        false
                    )
                }
                onConfirm={
                    handleCancelPurchaseBill
                }
            />
        </div>
    );
};

export default PurchaseBillViewPage;